import { styled } from "styled-components";

import {
  Image,
  MarkdownArea,
  MasterDetailContentWrapper,
  SectionTitleUnderlined,
  SourceLabel,
  StyledGrid,
  UIActionButton,
  UIButtonsRow,
} from "common/components";
import { ListItemWrapper } from "common/components/lists/ListItemWrapper";
import { SmallListItem } from "common/components/lists/SmallListItem";
import { breakpoints } from "common/styles";
import { hardNavigate } from "common/utilities/hardNavigate";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { shareLinkAction } from "domains/share/actions";
import type { EventSession } from "entities/events/types";
import { getCanAccessDiscoverForumEvents } from "entities/permissions/selectors";

const StyledTitle = styled.h2`
  font-size: 2rem;
  margin: 5px 0;
`;

const StyledContent = styled.div`
  height: 100%;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledButtons = styled(UIButtonsRow)`
  margin: 1rem 0;
`;

const StyledImage = styled(Image)`
  height: 200px;
  border-radius: 4px;

  @media ${breakpoints.medium} {
    height: 250px;
  }

  @media ${breakpoints.extraLarge} {
    height: 350px;
  }
`;

const StyledListItemWrapper = styled(ListItemWrapper)`
  margin: 0;
`;

const StyledSourceLabel = styled(SourceLabel)`
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
`;

type EventDetailProps = { event?: EventSession; shareLink: string };

export const EventDetail = ({ event, shareLink }: EventDetailProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const canViewToplinkEvent = useSelector(getCanAccessDiscoverForumEvents);

  if (!event) {
    return null;
  }

  const onViewEventClick = () => {
    hardNavigate(`${import.meta.env.VITE_TOPLINK_URL}/sessions/${event.id}/overview`);
  };

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: event.id,
        name: event.name,
        link: `${import.meta.env.VITE_WEBSITE_URL}${shareLink}`,
        type: "Session",
      }),
    );

  return (
    <>
      <MasterDetailContentWrapper>
        <StyledImage src={event.images?.medium} alt={event.name} />
        <StyledContent>
          <StyledSourceLabel source={event} date={event.from} isDateAbsolute />
          <StyledTitle>{event.name}</StyledTitle>
          <MarkdownArea>{event.description_markdown}</MarkdownArea>
          <StyledButtons>
            {canViewToplinkEvent && (
              <UIActionButton
                type="open"
                onClick={onViewEventClick}
                label={translate("app.discover.topic.tabs.events.view_event")}
              />
            )}
            <UIActionButton type="share" onClick={onShareClick} />
          </StyledButtons>
          {(event.speakers || []).length !== 0 && (
            <>
              <SectionTitleUnderlined>{translate("app.discover.topic.tabs.events.panelists")}</SectionTitleUnderlined>
              <StyledGrid numberOfItems={[1, 2, 2, 3, 3]}>
                {event.speakers.map((speaker) => (
                  <StyledListItemWrapper key={speaker.full_name} trackPage="Discover">
                    <SmallListItem
                      image={speaker.images?.thumbnail}
                      title={speaker.full_name}
                      subElement={<span>{speaker.primary_organisation}</span>}
                    />
                  </StyledListItemWrapper>
                ))}
              </StyledGrid>
            </>
          )}
        </StyledContent>
      </MasterDetailContentWrapper>
    </>
  );
};
